/**
 * Utility functions for processing images before upload
 */

/**
 * Estimates if an image is already compressed based on file size and dimensions
 * @param file The image file to analyze
 * @returns A promise that resolves to true if the image appears to be already compressed
 */
const isImageAlreadyCompressed = (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      
      img.onload = () => {
        // Calculate bytes per pixel
        const bytesPerPixel = file.size / (img.width * img.height);
        
        // If bytes per pixel is low, it's likely already compressed
        // Threshold: less than 0.5 bytes per pixel suggests compression
        const isCompressed = bytesPerPixel < 0.5;
        
        resolve(isCompressed);
      };
      
      img.onerror = () => {
        // If we can't analyze, assume it's not compressed to be safe
        resolve(false);
      };
    };
    
    reader.onerror = () => {
      resolve(false);
    };
  });
};

/**
 * Compresses an image and converts it to a Base64 data URL
 * Only compresses images larger than 4.5MB to preserve quality of already small images
 * Also avoids compressing images that appear to be already compressed
 * @param file The image file to process
 * @param maxWidth Maximum width of the compressed image (only used if compression is needed)
 * @param maxHeight Maximum height of the compressed image (only used if compression is needed)
 * @param quality Compression quality (0-1) (only used if compression is needed)
 * @returns A promise that resolves to the processed image as a Base64 data URL
 */
export const compressAndConvertToBase64 = (
  file: File,
  maxWidth = 300,
  maxHeight = 300,
  quality = 0.4
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      
      img.onload = () => {
        // Check if file size is larger than 4.5MB (4.5 * 1024 * 1024 = 4718592 bytes)
        const shouldCompressBySize = file.size > 4.5 * 1024 * 1024;
        
        // Check if image appears to be already compressed
        isImageAlreadyCompressed(file).then((isAlreadyCompressed) => {
          // Only compress if file is large AND not already compressed
          const shouldCompress = shouldCompressBySize && !isAlreadyCompressed;
          
          if (!shouldCompress) {
            // For images under 4.5MB or already compressed, return the original without compression
            // This preserves quality for already small/compressed images
            resolve(event.target?.result as string);
            return;
          }
          
          // Only compress images larger than 4.5MB that aren't already compressed
          // Create canvas
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }
          
          // Use the provided max dimensions instead of hardcoded values
          if (width > maxWidth || height > maxHeight) {
            const aspectRatio = width / height;
            if (width > height) {
              width = maxWidth;
              height = Math.round(width / aspectRatio);
            } else {
              height = maxHeight;
              width = Math.round(height * aspectRatio);
            }
          }
          
          // Set canvas dimensions
          canvas.width = width;
          canvas.height = height;
          
          // Draw image on canvas
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }
          
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to data URL with reduced quality
          const dataUrl = canvas.toDataURL('image/jpeg', quality);
          
          // Estimate the size of the base64 data
          const estimatedSize = Math.ceil((dataUrl.length - 22) * 0.75);
          
          // If it's still too large, reduce dimensions and quality further
          if (estimatedSize > 90000) { // Stay safely under 100KB
            // Try again with even smaller dimensions and lower quality
            const smallerCanvas = document.createElement('canvas');
            const smallerWidth = Math.floor(width * 0.7);
            const smallerHeight = Math.floor(height * 0.7);
            
            smallerCanvas.width = smallerWidth;
            smallerCanvas.height = smallerHeight;
            
            const smallerCtx = smallerCanvas.getContext('2d');
            if (smallerCtx) {
              smallerCtx.drawImage(img, 0, 0, smallerWidth, smallerHeight);
              const smallerDataUrl = smallerCanvas.toDataURL('image/jpeg', 0.3); // Use even lower quality
              resolve(smallerDataUrl);
            } else {
              // Fallback to original result if we can't create a second canvas
              resolve(dataUrl);
            }
          } else {
            resolve(dataUrl);
          }
        });
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
  });
};

/**
 * Converts a Base64 data URL to a File object
 * @param dataUrl The Base64 data URL to convert
 * @param filename The name to give the File
 * @returns A File object
 */
export const dataUrlToFile = (dataUrl: string, filename: string): File => {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)![1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  
  return new File([u8arr], filename, { type: mime });
}; 